/**
 * Utility for mapping API errors to user-friendly messages.
 */

const NETWORK_CODES = [
  'ERR_NETWORK',
  'ECONNREFUSED',
  'ENOTFOUND',
  'ECONNABORTED',
];

const DEFAULT_MESSAGE = 'An unexpected error occurred. Please try again.';

/** Normalise code/status/data from axios errors, RTK Query errors, or plain objects. */
const normalise = (error) => ({
  code: error.code || error.error?.code,
  status: error.status || error.error?.status,
  data: error.data || error.error?.data,
});

/**
 * Returns true when the error indicates a network-level failure
 * (no response received from the server at all).
 */
export const isNetworkError = (error) => {
  if (!error) return false;
  const {code} = normalise(error);
  return (
    (code && NETWORK_CODES.includes(code)) ||
    error.message === 'Network Error' ||
    (error.name === 'TypeError' && error.message === 'Failed to fetch')
  );
};

/**
 * Returns true for errors that are worth retrying (network issues, 5xx).
 */
export const isRetryableError = (error) => {
  if (!error) return false;
  if (isNetworkError(error)) return true;
  const {status} = normalise(error);
  return (status >= 500 && status < 600) || status === 429;
};

/**
 * Map an API error to a concise, user-friendly message.
 *
 * @param {Object} error  — axios error, RTK Query error, or plain object
 * @param {Object} [context]
 * @param {string} [context.resource] — e.g. "conversation" for 404 messages
 * @returns {string}
 */
export const getApiErrorMessage = (error, context = {}) => {
  if (!error) return DEFAULT_MESSAGE;

  const {code, status, data} = normalise(error);

  // Network / timeout
  if (isNetworkError(error)) {
    return code === 'ECONNABORTED'
      ? 'The request timed out. Please try again.'
      : 'Unable to connect. Check your internet connection and try again.';
  }

  // HTTP status codes
  if (status === 401)
    return 'Your session has expired. Please refresh the page.';
  if (status === 403)
    return 'You do not have permission to perform this action.';
  if (status === 404)
    return `This ${context.resource || 'resource'} could not be found.`;
  if (status === 429)
    return 'Too many requests. Please wait a moment and try again.';
  if (status >= 500 && status < 600)
    return 'Something went wrong on our end. Please try again.';

  // Backend-provided message
  if (typeof data === 'object' && data?.message) return data.message;
  if (typeof data === 'string' && data.length > 0 && data.length < 200)
    return data;

  return DEFAULT_MESSAGE;
};
